import React, { useEffect, useState, useCallback } from "react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { Plus, Loader2, Users, ClipboardList, AlertTriangle, Activity, Settings, FileText, ScrollText, BellRing, Layers, Wallet } from "lucide-react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import {
  REQUEST_TYPES,
  SERVICE_TYPES,
  ALL_STATUSES,
  STATUS_META,
  ROLE_LABELS,
  labelOf,
} from "../../lib/gmaoConstants";
import { INVOICE_STATUS_META, fmtEUR, isNoVat } from "../../lib/invoiceConstants";
import { QUOTE_STATUS_META } from "../../lib/quoteConstants";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import StatusBadge from "./StatusBadge";
import InterventionForm from "./InterventionForm";
import InterventionDetail from "./InterventionDetail";
import InvoiceForm from "./InvoiceForm";
import InvoiceDetail from "./InvoiceDetail";
import CompanySettingsDialog from "./CompanySettingsDialog";
import QuoteForm from "./QuoteForm";
import QuoteDetail from "./QuoteDetail";
import ReminderSettingsDialog from "./ReminderSettingsDialog";
import GroupedInvoiceManager from "./GroupedInvoiceManager";
import PaymentsTab from "./PaymentsTab";
import { INVOICE_STATUS_META as GRP_STATUS_META } from "../../lib/invoiceConstants";

const Kpi = ({ icon: Icon, label, value, color }) => (
  <Card className="p-4 flex items-center gap-3">
    <div className={`rounded-lg p-2 ${color}`}>
      <Icon className="w-5 h-5 text-white" />
    </div>
    <div>
      <p className="text-2xl font-bold leading-none">{value}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </div>
  </Card>
);

const fmtDate = (iso) => {
  try {
    return format(new Date(iso), "dd/MM/yy HH:mm", { locale: fr });
  } catch {
    return "-";
  }
};

const emptyUser = { name: "", email: "", password: "", phone: "", company: "", role: "technicien" };

const AdminDashboard = () => {
  const [interventions, setInterventions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [formOpen, setFormOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const [userFormOpen, setUserFormOpen] = useState(false);
  const [userForm, setUserForm] = useState(emptyUser);
  const [userSaving, setUserSaving] = useState(false);

  const [invoices, setInvoices] = useState([]);
  const [invoiceFormOpen, setInvoiceFormOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [invoiceDetailOpen, setInvoiceDetailOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const [quotes, setQuotes] = useState([]);
  const [quoteFormOpen, setQuoteFormOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteDetailOpen, setQuoteDetailOpen] = useState(false);

  const [companySettings, setCompanySettings] = useState(null);
  const [remindersOpen, setRemindersOpen] = useState(false);
  const [groupedInvoices, setGroupedInvoices] = useState([]);
  const [groupedManagerOpen, setGroupedManagerOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [intRes, userRes, invRes, quoteRes, setRes, grpRes] = await Promise.all([
        API.get("/interventions", { params: { limit: 200 } }),
        API.get("/users"),
        API.get("/invoices", { params: { limit: 200 } }),
        API.get("/quotes", { params: { limit: 200 } }),
        API.get("/settings/company"),
        API.get("/grouped-invoices"),
      ]);
      setInterventions(intRes.data.interventions);
      setUsers(userRes.data);
      setInvoices(invRes.data.invoices);
      setQuotes(quoteRes.data.quotes);
      setCompanySettings(setRes.data);
      setGroupedInvoices(grpRes.data.groupedInvoices);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const technicians = users.filter((u) => u.role === "technicien");
  const clients = users.filter((u) => u.role === "agence" || u.role === "client");

  const filtered =
    statusFilter === "all"
      ? interventions
      : interventions.filter((i) => i.status === statusFilter);

  const kpis = {
    total: interventions.length,
    nouvelles: interventions.filter((i) => i.status === "nouvelle_demande").length,
    urgentes: interventions.filter((i) => ["haute", "critique"].includes(i.urgency)).length,
    encours: interventions.filter((i) =>
      ["en_cours", "technicien_en_route", "intervention_programmee"].includes(i.status)
    ).length,
  };

  const openDetail = async (item) => {
    setSelected(item);
    setDetailOpen(true);
  };

  const handleUpdated = (updated) => {
    setInterventions((prev) => prev.map((i) => (i.id === updated.id ? updated : i)));
    setSelected(updated);
  };

  const handleInvoiceUpdated = (updated) => {
    setInvoices((prev) => prev.map((inv) => (inv.id === updated.id ? updated : inv)));
    setSelectedInvoice(updated);
  };

  const handleQuoteUpdated = (updated) => {
    setQuotes((prev) => prev.map((q) => (q.id === updated.id ? updated : q)));
    setSelectedQuote(updated);
  };

  const handleConverted = () => {
    toast.success("Facture créée — voir l'onglet Factures");
    load();
  };

  const grpAction = async (fn, okMsg) => {
    try {
      await fn();
      toast.success(okMsg);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const issueGrouped = (id) => grpAction(() => API.patch(`/grouped-invoices/${id}/status`, { status: "envoyee" }), "Facture groupée émise");
  const deleteGrouped = (id) => grpAction(() => API.delete(`/grouped-invoices/${id}`), "Facture groupée annulée");
  const downloadGroupedPdf = async (id) => {
    try {
      const res = await API.get(`/grouped-invoices/${id}/pdf`, { responseType: "blob" });
      const u = window.URL.createObjectURL(res.data);
      window.open(u, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(u), 10000);
    } catch { toast.error("PDF indisponible."); }
  };

  const createUser = async (e) => {
    e.preventDefault();
    setUserSaving(true);
    try {
      await API.post("/users", {
        name: userForm.name,
        email: userForm.email,
        password: userForm.password,
        phone: userForm.phone || null,
        company: userForm.company || null,
        role: userForm.role,
      });
      toast.success("Utilisateur créé");
      setUserForm(emptyUser);
      setUserFormOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setUserSaving(false);
    }
  };

  const setU = (k, v) => setUserForm((f) => ({ ...f, [k]: v }));
  const noVat = isNoVat(companySettings?.vatRegime);

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Tableau de bord</h1>
          <p className="text-sm text-muted-foreground">Gestion des interventions NGL SERVICES</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => setRemindersOpen(true)} data-testid="reminders-settings-button">
            <BellRing className="w-4 h-4 mr-2" /> Relances
          </Button>
          <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="company-settings-button">
            <Settings className="w-4 h-4 mr-2" /> Paramètres société
          </Button>
          <Button className="btn-primary" onClick={() => setFormOpen(true)} data-testid="admin-new-intervention">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle intervention
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={ClipboardList} label="Total dossiers" value={kpis.total} color="bg-[hsl(207,78%,42%)]" />
        <Kpi icon={Activity} label="Nouvelles demandes" value={kpis.nouvelles} color="bg-blue-500" />
        <Kpi icon={AlertTriangle} label="Urgentes" value={kpis.urgentes} color="bg-[hsl(24,95%,53%)]" />
        <Kpi icon={Activity} label="En cours" value={kpis.encours} color="bg-cyan-500" />
      </div>

      <Tabs defaultValue="interventions">
        <TabsList>
          <TabsTrigger value="interventions" data-testid="tab-interventions">Interventions</TabsTrigger>
          <TabsTrigger value="users" data-testid="tab-users">
            <Users className="w-4 h-4 mr-2" /> Utilisateurs
          </TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">
            <FileText className="w-4 h-4 mr-2" /> Factures
          </TabsTrigger>
          <TabsTrigger value="quotes" data-testid="tab-quotes">
            <ScrollText className="w-4 h-4 mr-2" /> Devis
          </TabsTrigger>
          <TabsTrigger value="grouped" data-testid="tab-grouped">
            <Layers className="w-4 h-4 mr-2" /> Fact. groupée
          </TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">
            <Wallet className="w-4 h-4 mr-2" /> Paiements
          </TabsTrigger>
        </TabsList>

        <TabsContent value="interventions" className="mt-4">
          <div className="flex items-center gap-3 mb-4">
            <Label className="text-sm">Filtrer :</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-64" data-testid="status-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                {ALL_STATUSES.map((s) => (
                  <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Card className="overflow-hidden">
            {loading ? (
              <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[hsl(207,78%,42%)]" /></div>
            ) : filtered.length === 0 ? (
              <p className="p-10 text-center text-muted-foreground">Aucune intervention.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dossier</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Service</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((i) => (
                    <TableRow
                      key={i.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => openDetail(i)}
                      data-testid={`intervention-row-${i.dossierNumber}`}
                    >
                      <TableCell className="font-mono text-xs font-semibold text-[hsl(207,78%,42%)]">{i.dossierNumber}</TableCell>
                      <TableCell className="text-xs">{fmtDate(i.createdAt)}</TableCell>
                      <TableCell className="text-sm">{i.clientName || i.createdByRole}</TableCell>
                      <TableCell className="text-sm">{labelOf(SERVICE_TYPES, i.serviceType)}</TableCell>
                      <TableCell><StatusBadge status={i.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="users" className="mt-4">
          <div className="flex justify-end mb-4">
            <Button className="btn-primary" onClick={() => setUserFormOpen(true)} data-testid="admin-new-user">
              <Plus className="w-4 h-4 mr-2" /> Créer un utilisateur
            </Button>
          </div>
          <Card className="overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Société</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.email}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-sm">{u.email}</TableCell>
                    <TableCell className="text-sm">{ROLE_LABELS[u.role] || u.role}</TableCell>
                    <TableCell className="text-sm">{u.company || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="invoices" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-muted-foreground max-w-xl">
              Facturation conforme aux mentions obligatoires. Export PDF + données structurées (préparation). La transmission B2B officielle passe par une plateforme agréée (PDP).
            </p>
            <Button className="btn-primary" onClick={() => setInvoiceFormOpen(true)} data-testid="admin-new-invoice">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle facture
            </Button>
          </div>
          <Card className="overflow-hidden">
            {invoices.length === 0 ? (
              <p className="p-10 text-center text-muted-foreground">Aucune facture.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Émission</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Total TTC</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow
                      key={inv.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => { setSelectedInvoice(inv); setInvoiceDetailOpen(true); }}
                      data-testid={`invoice-row-${inv.id}`}
                    >
                      <TableCell className="font-mono text-xs font-semibold text-[hsl(207,78%,42%)]">{inv.number || "—"}</TableCell>
                      <TableCell className="text-xs">{inv.issueDate}</TableCell>
                      <TableCell className="text-sm">{inv.buyer?.name}</TableCell>
                      <TableCell className="text-sm font-medium">{fmtEUR(inv.totalTTC)}</TableCell>
                      <TableCell>
                        <span className={`inline-block text-xs px-2 py-0.5 rounded border ${INVOICE_STATUS_META[inv.status]?.color || ""}`}>
                          {INVOICE_STATUS_META[inv.status]?.label || inv.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>

        <TabsContent value="quotes" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-muted-foreground max-w-xl">
              Devis professionnels avec validation client en ligne. Un devis accepté peut être converti en facture.
            </p>
            <Button className="btn-primary" onClick={() => setQuoteFormOpen(true)} data-testid="admin-new-quote">
              <Plus className="w-4 h-4 mr-2" /> Nouveau devis
            </Button>
          </div>
          <Card className="overflow-hidden">
            {quotes.length === 0 ? (
              <p className="p-10 text-center text-muted-foreground">Aucun devis.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Total TTC</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {quotes.map((q) => (
                    <TableRow
                      key={q.id}
                      className="cursor-pointer hover:bg-slate-50"
                      onClick={() => { setSelectedQuote(q); setQuoteDetailOpen(true); }}
                      data-testid={`quote-row-${q.id}`}
                    >
                      <TableCell className="font-mono text-xs font-semibold text-[hsl(207,78%,42%)]">{q.number || "—"}</TableCell>
                      <TableCell className="text-xs">{q.issueDate}</TableCell>
                      <TableCell className="text-sm">{q.buyer?.name}</TableCell>
                      <TableCell className="text-sm font-medium">{fmtEUR(q.totalTTC)}</TableCell>
                      <TableCell>
                        <span className={`inline-block text-xs px-2 py-0.5 rounded border ${QUOTE_STATUS_META[q.status]?.color || ""}`}>
                          {QUOTE_STATUS_META[q.status]?.label || q.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>


        <TabsContent value="grouped" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <p className="text-xs text-muted-foreground max-w-xl">
              Regroupez plusieurs interventions terminées d'un même client en une seule facture globale (FG-…).
            </p>
            <Button className="btn-primary" onClick={() => setGroupedManagerOpen(true)} data-testid="admin-new-grouped">
              <Plus className="w-4 h-4 mr-2" /> Nouvelle facture groupée
            </Button>
          </div>
          <Card className="overflow-hidden">
            {groupedInvoices.length === 0 ? (
              <p className="p-10 text-center text-muted-foreground">Aucune facture groupée.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Numéro</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Interventions</TableHead>
                    <TableHead>Net à payer</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedInvoices.map((g) => (
                    <TableRow key={g.id} data-testid={`grouped-row-${g.id}`}>
                      <TableCell className="font-mono text-xs font-semibold text-[hsl(207,78%,42%)]">{g.number || "—"}</TableCell>
                      <TableCell className="text-sm">{g.buyer?.name}</TableCell>
                      <TableCell className="text-sm">{g.lines?.length}</TableCell>
                      <TableCell className="text-sm font-medium">{fmtEUR(g.totalTTC)}</TableCell>
                      <TableCell>
                        <span className={`inline-block text-xs px-2 py-0.5 rounded border ${GRP_STATUS_META[g.status]?.color || ""}`}>
                          {GRP_STATUS_META[g.status]?.label || g.status}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1 flex-wrap">
                          <Button variant="outline" size="sm" onClick={() => downloadGroupedPdf(g.id)} data-testid={`grouped-pdf-${g.id}`}>PDF</Button>
                          {g.status === "brouillon" && (
                            <>
                              <Button size="sm" className="btn-primary" onClick={() => issueGrouped(g.id)} data-testid={`grouped-issue-${g.id}`}>Émettre</Button>
                              <Button variant="ghost" size="sm" className="text-red-500" onClick={() => deleteGrouped(g.id)} data-testid={`grouped-delete-${g.id}`}>Annuler</Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Card>
        </TabsContent>


        <TabsContent value="payments" className="mt-4">
          <PaymentsTab />
        </TabsContent>

      </Tabs>

      <InterventionForm
        open={formOpen}
        onOpenChange={setFormOpen}
        onCreated={() => load()}
        clients={clients}
        showTenant
      />

      <InterventionDetail
        open={detailOpen}
        onOpenChange={setDetailOpen}
        intervention={selected}
        role="admin"
        technicians={technicians}
        onUpdated={handleUpdated}
      />

      <InvoiceForm
        open={invoiceFormOpen}
        onOpenChange={setInvoiceFormOpen}
        onCreated={() => load()}
        clients={clients}
        interventions={interventions}
        noVat={noVat}
      />

      <InvoiceDetail
        open={invoiceDetailOpen}
        onOpenChange={setInvoiceDetailOpen}
        invoice={selectedInvoice}
        role="admin"
        onUpdated={handleInvoiceUpdated}
      />

      <CompanySettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />

      <ReminderSettingsDialog open={remindersOpen} onOpenChange={setRemindersOpen} />

      <GroupedInvoiceManager
        open={groupedManagerOpen}
        onOpenChange={setGroupedManagerOpen}
        clients={clients}
        onCreated={() => load()}
      />

      <QuoteForm
        open={quoteFormOpen}
        onOpenChange={setQuoteFormOpen}
        onCreated={() => load()}
        clients={clients}
        interventions={interventions}
        noVat={noVat}
      />

      <QuoteDetail
        open={quoteDetailOpen}
        onOpenChange={setQuoteDetailOpen}
        quote={selectedQuote}
        role="admin"
        onUpdated={handleQuoteUpdated}
        onConverted={handleConverted}
      />

      {/* Création utilisateur */}
      <Dialog open={userFormOpen} onOpenChange={setUserFormOpen}>
        <DialogContent data-testid="user-form-dialog">
          <DialogHeader>
            <DialogTitle>Créer un utilisateur</DialogTitle>
            <DialogDescription>
              Renseignez les informations du compte. Un email valide et un mot de passe d'au moins 6 caractères sont requis.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createUser} className="space-y-4">
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select value={userForm.role} onValueChange={(v) => setU("role", v)}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="technicien">Technicien</SelectItem>
                  <SelectItem value="agence">Agence / Professionnel</SelectItem>
                  <SelectItem value="client">Client particulier</SelectItem>
                  <SelectItem value="admin">Administrateur</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nom complet</Label>
              <Input value={userForm.name} onChange={(e) => setU("name", e.target.value)} required data-testid="user-name-input" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={userForm.email} onChange={(e) => setU("email", e.target.value)} required data-testid="user-email-input" />
            </div>
            <div className="space-y-2">
              <Label>Mot de passe</Label>
              <Input type="text" value={userForm.password} onChange={(e) => setU("password", e.target.value)} required minLength={6} data-testid="user-password-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input value={userForm.phone} onChange={(e) => setU("phone", e.target.value)} data-testid="user-phone-input" />
              </div>
              <div className="space-y-2">
                <Label>Société</Label>
                <Input value={userForm.company} onChange={(e) => setU("company", e.target.value)} data-testid="user-company-input" />
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setUserFormOpen(false)}>Annuler</Button>
              <Button type="submit" className="btn-primary" disabled={userSaving} data-testid="user-form-submit">
                {userSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Créer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
